/**
 * DV-STOR-002: Stage Outputs React Query Hook
 *
 * Hook for fetching and managing stage outputs data with API-first approach,
 * aligned with INF-001/002 patterns using HTTP endpoints and proper headers.
 */

import { useQuery } from '@tanstack/react-query';
import { buildApiUrl, API_ENDPOINTS, getApiHeadersGet } from '@/lib/api-base';
import { getDemoError } from '@/lib/demo-error-handler';
import type { StageOutput, StageOutputsApiResponse } from '@/types/stage-outputs';

export const stageOutputsKeys = {
  all: ['stage-outputs'] as const,
  execution: (executionId: string) => [...stageOutputsKeys.all, 'execution', executionId] as const,
  stage: (executionId: string, stage: string) => [...stageOutputsKeys.execution(executionId), 'stage', stage] as const,
};

// Track recent API calls for status reports
class StageOutputsAPIClient {
  private recentApiCalls: Array<{
    method: string;
    path: string;
    status: number;
    elapsed: number;
    requestId?: string;
    timestamp: string;
  }> = [];

  private logApiCall(method: string, path: string, status: number, elapsed: number, requestId?: string) {
    this.recentApiCalls.unshift({
      method,
      path: path.replace(API_ENDPOINTS.demo.storageOutputs(''), '/api/demo/storage/outputs'),
      status,
      elapsed,
      requestId,
      timestamp: new Date().toISOString()
    });

    // Keep only last 10 calls
    if (this.recentApiCalls.length > 10) {
      this.recentApiCalls = this.recentApiCalls.slice(0, 10);
    }
  }

  getRecentApiCalls() {
    return [...this.recentApiCalls];
  }

  async fetchStageOutputs(executionId: string): Promise<StageOutputsApiResponse> {
    const url = buildApiUrl(API_ENDPOINTS.demo.storageOutputs(executionId));
    const startTime = performance.now();

    const response = await fetch(url, {
      credentials: 'include',
      headers: getApiHeadersGet(), // No Content-Type for GET requests
      cache: 'no-store', // API-first policy
    });

    const elapsed = Math.round(performance.now() - startTime);
    const requestId = response.headers.get('X-Request-Id') || undefined;

    this.logApiCall('GET', API_ENDPOINTS.demo.storageOutputs(executionId), response.status, elapsed, requestId);

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        message: `HTTP ${response.status}: ${response.statusText}`
      }));

      const demoError = getDemoError({
        message: error.message || 'Failed to fetch stage outputs',
        status: response.status,
        code: error.code,
        details: error.details
      });

      throw demoError;
    }

    const data = await response.json();

    // Backend now returns StageOutputsApiResponse format directly
    return data as StageOutputsApiResponse;
  }
}

// Export singleton instance
export const stageOutputsAPIClient = new StageOutputsAPIClient();

// Fetch all stage outputs for an execution
export function useStageOutputs(executionId: string) {
  return useQuery({
    queryKey: stageOutputsKeys.execution(executionId),
    queryFn: () => stageOutputsAPIClient.fetchStageOutputs(executionId),
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!executionId,
  });
}

// Fetch outputs for a specific stage
export function useStageOutputsByStage(executionId: string, stage: string) {
  return useQuery({
    queryKey: stageOutputsKeys.stage(executionId, stage),
    queryFn: async (): Promise<StageOutput[]> => {
      // Use the main API client and filter by stage
      const response = await stageOutputsAPIClient.fetchStageOutputs(executionId);
      return response.outputs_by_stage[stage] || [];
    },
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!executionId && !!stage,
  });
}